/**
 * Shared hook for kernel consumers.
 *
 * Both the Code Interface and Antioch Agent use this hook to interact
 * with the KernelPool. They each independently start, attach to,
 * and detach from kernels.
 */

import * as React from 'react';
import type { KernelHealth } from '@/lib/jupyter/JupyterClient';
import type { KernelHandle, KernelInfo, KernelSpecInfo } from '@/lib/jupyter/KernelPool';
import { KernelNotAvailableError } from '@/lib/jupyter/KernelPool';
import { useKernelPool } from '@/providers/KernelPoolProvider';

export type KernelOwnership = 'owned' | 'attached' | 'none';

export interface KernelConsumerState {
  handle: KernelHandle | null;
  availableKernels: KernelInfo[];
  /** Kernelspecs registered on the Jupyter server, for the "new kernel" picker. */
  availableKernelSpecs: KernelSpecInfo[];
  /** Name of the server's default kernelspec, or null until fetched. */
  defaultKernelSpec: string | null;
  startNew: (kernelspec?: string, displayName?: string) => Promise<KernelHandle>;
  attachTo: (kernelId: string) => Promise<KernelHandle>;
  detach: () => void;
  health: KernelHealth;
  adoptHandle: (handle: KernelHandle, ownership?: KernelOwnership) => void;
  ownership: KernelOwnership;
}

const DEFAULT_HEALTH: KernelHealth = {
  status: 'unknown',
  connectionStatus: 'disconnected',
  isHealthy: false,
};

export function useKernelConsumer(consumerId: string): KernelConsumerState {
  const { pool, kernels, kernelSpecs, defaultKernelSpec, isConnected } = useKernelPool();
  const [handle, setHandle] = React.useState<KernelHandle | null>(null);
  const [health, setHealth] = React.useState<KernelHealth>(DEFAULT_HEALTH);
  const [ownership, setOwnership] = React.useState<KernelOwnership>('none');
  const handleRef = React.useRef<KernelHandle | null>(null);

  // Track health of the attached kernel
  React.useEffect(() => {
    if (!handle) {
      setHealth(DEFAULT_HEALTH);
      return;
    }

    const unsubscribe = handle.onHealthChange((h) => setHealth(h));
    return () => unsubscribe();
  }, [handle]);

  // Detach when pool disconnects
  React.useEffect(() => {
    if (!isConnected && handleRef.current) {
      handleRef.current.detach();
      handleRef.current = null;
      setHandle(null);
      setOwnership('none');
    }
  }, [isConnected]);

  const startNew = React.useCallback(
    async (kernelspec?: string, displayName?: string): Promise<KernelHandle> => {
      if (!pool) throw new KernelNotAvailableError();

      if (handleRef.current) {
        handleRef.current.detach();
      }

      const newHandle = await pool.startKernel(consumerId, kernelspec, displayName);
      handleRef.current = newHandle;
      setHandle(newHandle);
      setOwnership('owned');
      return newHandle;
    },
    [pool, consumerId]
  );

  const attachTo = React.useCallback(
    async (kernelId: string): Promise<KernelHandle> => {
      if (!pool) throw new KernelNotAvailableError();

      if (handleRef.current) {
        handleRef.current.detach();
      }

      const newHandle = await pool.connectToKernel(kernelId, consumerId);
      handleRef.current = newHandle;
      setHandle(newHandle);
      setOwnership('attached');
      return newHandle;
    },
    [pool, consumerId]
  );

  const detach = React.useCallback(() => {
    if (handleRef.current) {
      handleRef.current.detach();
      handleRef.current = null;
      setHandle(null);
      setOwnership('none');
    }
  }, []);

  const adoptHandle = React.useCallback((next: KernelHandle, nextOwnership: KernelOwnership = 'owned') => {
    if (handleRef.current && handleRef.current !== next) {
      handleRef.current.detach();
    }
    handleRef.current = next;
    setHandle(next);
    setOwnership(nextOwnership);
  }, []);

  // Clean up on unmount
  React.useEffect(() => {
    return () => {
      if (handleRef.current) {
        handleRef.current.detach();
        handleRef.current = null;
      }
    };
  }, []);

  return {
    handle,
    availableKernels: kernels,
    availableKernelSpecs: kernelSpecs,
    defaultKernelSpec,
    startNew,
    attachTo,
    detach,
    health,
    adoptHandle,
    ownership,
  };
}
